<script setup lang="ts">
import { ref, watch } from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import type { VisionProject } from '@/models/RobVision';
import { triggerPartTraining, type TrainingAllowed } from '@/api/RobVisionApi';
import { useAlertsStore } from '@/store/AlertsStore';
import TrainingModelChoiceDialog from '@/components/robvision/TrainingModelChoiceDialog.vue';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{
  isOpen: boolean;
  project: VisionProject | null;
  // Per-generation entitlement; null while loading (both choices enabled).
  trainingAllowed?: TrainingAllowed | null;
}>();
const emit = defineEmits(['update:isOpen', 'training-triggered']);
const loadingSpinnerStore = useLoadingSpinnerStore();
const alertsStore = useAlertsStore();

const triggerError = ref('');
const successDialog = ref(false);
const submitting = ref(false);
// Opening asks which model to train first; the confirm card below is only
// shown for the current model (the deprecated card is its own confirmation).
const choiceOpen = ref(false);
const confirmOpen = ref(false);

watch(
  () => props.isOpen,
  (open) => {
    choiceOpen.value = open;
    if (!open) {
      confirmOpen.value = false;
    }
  },
);

// The choice closed without picking the current model: cancelled or the
// deprecated training already fired.
watch(choiceOpen, (open) => {
  if (!open && props.isOpen && !confirmOpen.value && !submitting.value) {
    cancel();
  }
});

const cancel = () => {
  emit('update:isOpen', false);
  triggerError.value = '';
};

// resolutionAngle set = train the deprecated Mask R-CNN model instead.
const confirm = async (resolutionAngle?: number) => {
  // In-flight guard: a second click before the overlay renders must not
  // trigger a second training run.
  if (!props.project || submitting.value) {
    return;
  }
  submitting.value = true;
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await triggerPartTraining(props.project.uuid, { resolutionAngle });
    emit('training-triggered');
    cancel();
    successDialog.value = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Failed to trigger part training:', error);
    const message =
      error?.message || $gettext('The training could not be started');
    if (confirmOpen.value) {
      triggerError.value = message;
    } else {
      // Deprecated path: no card is open to show the error in.
      alertsStore.error(message);
      cancel();
    }
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
    submitting.value = false;
  }
};

const closeSuccessDialog = () => {
  successDialog.value = false;
};
</script>

<template>
  <TrainingModelChoiceDialog
    v-model:isOpen="choiceOpen"
    :current-model-allowed="trainingAllowed?.currentModelAllowed ?? true"
    :deprecated-model-allowed="trainingAllowed?.deprecatedModelAllowed ?? true"
    @current="confirmOpen = true"
    @deprecated="confirm"
  />

  <!-- Esc closes the dialog internally; without syncing the parent flag the
       dialog could not be reopened. -->
  <v-dialog
    :model-value="confirmOpen"
    max-width="600"
    @update:model-value="(open: boolean) => !open && cancel()"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          icon="mdi-checkbox-marked-circle-outline"
          color="primary"
          size="small"
          class="mr-2"
        />
        {{ $gettext('Confirm Project Training') }}
      </v-card-title>
      <v-card-text>
        <p>
          {{
            $gettext(
              'Are you sure you want to run a new training for this project?',
            )
          }}
        </p>
        <p>
          <b>{{ project?.name }}</b>
        </p>
      </v-card-text>
      <v-alert v-if="triggerError" class="mx-2" type="error">{{
        triggerError
      }}</v-alert>
      <v-card-actions>
        <r-btn variant="outlined" :disabled="submitting" @click="cancel">
          {{ $gettext('Close') }}
        </r-btn>
        <v-spacer></v-spacer>
        <r-btn
          variant="accent"
          icon="mdi-clock-start"
          :loading="submitting"
          @click="confirm()"
        >
          {{ $gettext('Start Training') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="successDialog" max-width="600">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          icon="mdi-check-circle-outline"
          color="success"
          size="small"
          class="mr-2"
        />
        {{ $gettext('Training Triggered') }}
      </v-card-title>
      <v-card-text>
        <p>
          {{
            $gettext(
              'The training process will start. Follow the status in the project section.',
            )
          }}
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <r-btn variant="accent" @click="closeSuccessDialog">
          {{ $gettext('OK') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
